import { loadStripe } from '@stripe/stripe-js';
import api from './authService';

const CACHE_TTL_MS = 60 * 60 * 1000;

const cacheStorageKey = () =>
  `stripe_public_config_${process.env.REACT_APP_API_URL || 'default'}`;

const stripePromiseByKey = new Map();

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(cacheStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data?.publishable_key || !parsed?.cachedAt) return null;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    sessionStorage.setItem(
      cacheStorageKey(),
      JSON.stringify({ data, cachedAt: Date.now() })
    );
  } catch {
    // sessionStorage may be unavailable
  }
};

export const clearStripeConfigCache = () => {
  try {
    sessionStorage.removeItem(cacheStorageKey());
  } catch {
    // ignore
  }
  stripePromiseByKey.clear();
};

let inflightConfigRequest = null;

/**
 * Publishable key + mode from vendor-booking-api (single source of truth).
 */
export const fetchStripePublicConfig = async ({ force = false } = {}) => {
  if (!force) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (!inflightConfigRequest) {
    inflightConfigRequest = api
      .get('/stripe/config')
      .then((response) => {
        const payload = response.data;
        if (!payload?.success || !payload?.data?.publishable_key) {
          throw new Error(payload?.message || 'Stripe is not configured on the server');
        }
        writeCache(payload.data);
        return payload.data;
      })
      .finally(() => {
        inflightConfigRequest = null;
      });
  }

  return inflightConfigRequest;
};

export const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  if (!stripePromiseByKey.has(publishableKey)) {
    stripePromiseByKey.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromiseByKey.get(publishableKey);
};

export const isNetworkFailure = (error) =>
  !error?.response && Boolean(error?.request);
